import {
    Button,
    Drawer,
    DrawerProps,
    Group,
    ScrollArea,
    Select,
    Stack,
} from '@mantine/core';
import {Formik} from 'formik';
import {I18n} from '@aws-amplify/core';

import {
    PricesFilters,
    usePricesFilter,
} from '../hooks/usePricesFilter';

/**
 * FNP-918: the prices filter drawer (§7).
 *
 * Formik-driven, matching the form conventions in §6. Filter state lives in the
 * MODULE-SCOPED store (usePricesFilter), not component state, so the table — a
 * sibling, not a child — filters the same list.
 *
 * ⚠️ Two fields are offered, both genuine enum/boolean states on the record:
 * `active` and `currency`. Free-text fields are deliberately absent — a filter
 * control for `nickname` would duplicate the keyword search, and building one
 * would be serving a hypothetical use case.
 *
 * ⚠️ Filtering here is CLIENT-SIDE over the loaded window. `PricesFilterInput`
 * does not declare these fields — the one thing it does narrow server-side is
 * `product`, which the product detail page supplies through the route param
 * rather than through this drawer. See usePrices.ts.
 */
export const FilterDrawer = ({
                                 opened,
                                 onClose,
                                 ...rest
                             }: DrawerProps) => {

    const {
        filters,
        handleSetFilters,
        handleClearFilters,
    } = usePricesFilter();

    return (
        <Drawer
            transitionProps={{
                transition: 'slide-left',
                duration: 750,
                timingFunction: 'ease',
            }}
            position='right'
            offset={10}
            opened={opened}
            onClose={onClose}
            title={I18n.get('Filter Prices')}
            padding='xl'
            size='md'
            scrollAreaComponent={ScrollArea.Autosize}
            {...rest}
        >
            <Formik<PricesFilters>
                initialValues={filters}
                enableReinitialize
                onSubmit={(values) => {
                    handleSetFilters(values);
                    onClose();
                }}
            >
                {({
                      values,
                      setFieldValue,
                      handleSubmit,
                      resetForm,
                  }) => (
                    <form onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}>
                        <Stack gap='md'>
                            <Select
                                label={I18n.get('Active')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    {
                                        value: 'true',
                                        label: I18n.get('Active'),
                                    },
                                    {
                                        value: 'false',
                                        label: I18n.get('Inactive'),
                                    },
                                ]}
                                value={values.active ?? null}
                                onChange={(value) =>
                                    setFieldValue('active', value ?? '')}
                            />

                            <Select
                                label={I18n.get('Currency')}
                                placeholder={I18n.get('Any')}
                                clearable
                                data={[
                                    {
                                        value: 'usd',
                                        label: I18n.get('USD'),
                                    },
                                    {
                                        value: 'eur',
                                        label: I18n.get('EUR'),
                                    },
                                    {
                                        value: 'gbp',
                                        label: I18n.get('GBP'),
                                    },
                                ]}
                                value={values.currency ?? null}
                                onChange={(value) =>
                                    setFieldValue('currency', value ?? '')}
                            />

                            <Group justify='flex-end' mt='md'>
                                {/*
                                  * Clears EVERY field and is reachable from the
                                  * drawer (§7) — it resets both the store and
                                  * the visible form, so the two cannot disagree.
                                  */}
                                <Button
                                    variant='subtle'
                                    onClick={() => {
                                        handleClearFilters();
                                        resetForm({values: {}});
                                    }}
                                >
                                    {I18n.get('Clear filters')}
                                </Button>
                                <Button type='submit'>
                                    {I18n.get('Apply')}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                )}
            </Formik>
        </Drawer>
    );
};
